import { Currency, CurrencyAmount } from '@kodiak-finance/sdk-core'
import { SupportedChainId } from 'config/chains'
import { REFETCH_POLICY_FAST, RefetchPolicy } from 'config/RefetchPolicies'
import { PRE_TGE_XKDK_ADDRESSES, XKDK_ADDRESSES } from 'constants/addresses'
import { BGT_ADDRESS } from 'constants/tokens'
import { unsupportedSwapTokens } from 'constants/unsupportedSwapTokens'
import { useAllTokens } from 'hooks/Tokens'
import { useConnection } from 'hooks/useConnection'
import { useGetTokenPrices } from 'hooks/useTokenPrice'
import { useMemo } from 'react'
import { useTokenBalancesWithLoadingIndicator } from 'state/connection/hooks'
import { isLPToken } from 'utils/isLPToken'

export interface CurrencyWithBalanceAndPrice {
  currency: Currency
  balance: CurrencyAmount<Currency>
  price: number
}

export interface UseTokensWithBalanceAndPriceOptions {
  refetchPolicy?: RefetchPolicy
  enableLpTokens?: boolean
  exclude?: string[]
}

/**
 * Hook to get all tokens with their balances and prices, sorted by USD value
 */
export function useTokensWithBalanceAndPriceFast(options?: UseTokensWithBalanceAndPriceOptions) {
  const { account, chainId } = useConnection()
  const refetchPolicy = options?.refetchPolicy
  const preTgeXkdkAddress = PRE_TGE_XKDK_ADDRESSES[chainId as SupportedChainId]
  const xkdkAddress = XKDK_ADDRESSES[chainId as SupportedChainId]
  const defaultExclude = useMemo(
    () => [preTgeXkdkAddress, xkdkAddress, BGT_ADDRESS].filter(Boolean),
    [preTgeXkdkAddress, xkdkAddress]
  )
  const exclude = useMemo(
    () => options?.exclude ?? [...unsupportedSwapTokens, ...defaultExclude],
    [options?.exclude, defaultExclude]
  )
  const allTokens = useAllTokens(exclude)

  const tokensList = useMemo(() => {
    const allTokensList = Object.values(allTokens)
    if (options?.enableLpTokens === false) {
      return allTokensList.filter((token) => !isLPToken(token))
    }
    return allTokensList
  }, [allTokens, options?.enableLpTokens])

  // Get balances for all tokens
  // Note: this hook has fast refresh as it's used for multiswap
  const [balances, balancesIsLoading] = useTokenBalancesWithLoadingIndicator(
    account ?? undefined,
    tokensList,
    refetchPolicy ?? REFETCH_POLICY_FAST
  )

  // Get prices for all tokens
  const tokenAddresses = useMemo(() => tokensList.map((t) => t.address.toLowerCase()), [tokensList])
  const { prices, isLoading: pricesLoading } = useGetTokenPrices(tokenAddresses)

  // Combine tokens with their balances and prices
  const tokensWithData = useMemo(() => {
    if (!balances || !prices || pricesLoading || balancesIsLoading) return []

    return tokensList
      .map((token, index) => {
        const balance = balances[token.address]
        const price = prices[index] ?? 0

        const item: CurrencyWithBalanceAndPrice = {
          currency: token,
          balance: balance ?? CurrencyAmount.fromRawAmount(token, '0'),
          price,
        }
        return item
      })
      .filter((t): t is CurrencyWithBalanceAndPrice => t !== null)
      .sort((a, b) => {
        const balanceUSD_A = parseFloat(a.balance.toExact()) * a.price
        const balanceUSD_B = parseFloat(b.balance.toExact()) * b.price
        return balanceUSD_B - balanceUSD_A
      })
  }, [tokensList, balances, prices, pricesLoading, balancesIsLoading])

  return {
    tokensWithData,
    isLoading: pricesLoading || balancesIsLoading,
  }
}
